// B388 (AI_156) — PROVENIÊNCIA DA GERAÇÃO da análise geral.
// O backend devolve `_proveniencia` (modelo, esforço, versão do app, duração)
// em TODA resposta do /api/analyze, mas a análise geral descartava o campo ao
// salvar — os laudos e o Laudo Especial já gravam. Sem isso não havia como
// provar, pela própria análise, qual modelo a gerou (a validação da Fase 1 do
// B359 teve que sair pela lista de deploys da Vercel).
// Puro e Firestore-safe: nunca devolve `undefined` dentro do objeto; devolve
// `null` quando o backend não mandou nada (análise antiga / proveniência falhou).

export interface ProvenienciaGeracao {
  modelo: string;
  esforco: string;
  versaoApp: string;
  duracaoEtapa1Ms: number | null;
  duracaoEtapa2Ms: number | null;
}

/** Forma (parcial) do `_proveniencia` que `api/analyze.js` anexa à resposta. */
export interface ProvenienciaBackend {
  modelo?: string;
  versaoApp?: string;
  duracaoMs?: number;
  thinking?: { effort?: string };
}

const texto = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;
const numero = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null;

/**
 * Monta a proveniência da análise geral a partir das 2 etapas.
 * Modelo/esforço/versão vêm da ETAPA 1 (é a que roda no esforço configurado;
 * a Etapa 2 roda sempre em `low`). Se a Etapa 1 não trouxe, usa a Etapa 2.
 */
export function montarProvenienciaGeracao(
  etapa1?: ProvenienciaBackend | null,
  etapa2?: ProvenienciaBackend | null
): ProvenienciaGeracao | null {
  const fonte = etapa1?.modelo ? etapa1 : etapa2?.modelo ? etapa2 : null;
  if (!fonte) return null;
  return {
    modelo: texto(fonte.modelo) || "desconhecido",
    esforco: texto(fonte.thinking?.effort) || "desconhecido",
    versaoApp: texto(fonte.versaoApp) || "desconhecida",
    duracaoEtapa1Ms: numero(etapa1?.duracaoMs),
    duracaoEtapa2Ms: numero(etapa2?.duracaoMs),
  };
}

/** Nome legível do modelo pelo id (fallback: o próprio id). */
export function nomeLegivelDoModelo(id: string): string {
  const m = id.match(/^claude-(fable|opus|sonnet|haiku)-(\d+)(?:-(\d+))?/);
  if (!m) return id;
  const familia = m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return `${familia} ${m[2]}${m[3] ? "." + m[3] : ""}`;
}

const ESFORCO_PT: Record<string, string> = {
  low: "baixo",
  medium: "médio",
  high: "alto",
  xhigh: "muito alto",
  max: "máximo",
};

/** Linha única para o painel: "Opus 5.5 · esforço médio · versão 2d23919 · 3min41s". */
export function descreverProveniencia(p: ProvenienciaGeracao): string {
  const partes = [
    nomeLegivelDoModelo(p.modelo),
    `esforço ${ESFORCO_PT[p.esforco] || p.esforco}`,
    `versão ${p.versaoApp}`,
  ];
  const total = (p.duracaoEtapa1Ms || 0) + (p.duracaoEtapa2Ms || 0);
  if (total > 0) {
    const s = Math.round(total / 1000);
    partes.push(s >= 60 ? `${Math.floor(s / 60)}min${String(s % 60).padStart(2, "0")}s` : `${s}s`);
  }
  return partes.join(" · ");
}
